import {
  filter,
  find,
  head,
  includes,
  isEmpty,
  length,
  mergeAll,
  pathOr,
  propEq,
  replace,
  split,
  equals,
  isNil,
  lt,
  pipe,
  map,
  join,
  trim,
  not,
  test,
  when
} from 'ramda';
import amortize from 'amortize';
import { STANDARD_DOWNPAYMENT_PERCENTAGES } from 'src/utilities/constants';

const DASHBOARD_USER_MOVEMENT_INTEREST_RATE_UP = 0.25;

export const getRecurringPayments = (details: any) => {
  try {
    return find(propEq('name', 'recurring_deductions'))(
      pathOr([], ['items'], head(details))
    );
  } catch (e) {
    return 0;
  }
};



export const meaningfulNameInEmail = (email: string) => {
  const mailName = head(split('@', email));
  if (!isEmpty(mailName)) {
    return mailName;
  }
  return '';
};

export const getGrossPay = (monthlyIncomes: any) => {
  const incomes =
    !isNil(monthlyIncomes) && !isEmpty(monthlyIncomes) ? monthlyIncomes : [];
  let grossPay: number = 0;
  incomes.forEach((monthlyIncome: any) => {
    const pay = parseFloat(pathOr('', ['wages', 'gross'], monthlyIncome));
    // const multipier = Number(pathOr(1, ["multiplier"], monthlyIncome));

    // For Arvest bank
    const multipier = 2;
    grossPay = grossPay + pay * multipier;
  });
  return grossPay;
};

export const getMultiplier = (monthlyIncomes: any) => {
  return pathOr(1, ['multiplier'], head(monthlyIncomes));
};

export const calculateDTI = (
  downPaymentPercent: number,
  homePrice: number,
  grossPay: any,
  liability: any,
  recurringPayments: any
) => {
  let PMI: number = 0.0;
  let gpay: number = 1.0;
  // 31686.72
  // const interestRate = pathOr(0, ['interestRate'], data);
  // const homePrice = pathOr(0, ['homePrice'], data);
  // const grossPay = pathOr(0, ['grossPay'], data);
  // const multiplier = pathOr(0, ['multiplier'], data);
  // const liability = pathOr(0, ['liability'], data);
  // const recurringPayments = pathOr(0, ['recurringPayments'], data);

  const downPayment: number = (homePrice * downPaymentPercent) / 100;
  const loanValue: number = homePrice - downPayment;
  const loan: any = amortize({
    amount: loanValue, // amount
    totalTerm: 30 * 12, // installments number - 30 years
    rate: 4.5, // interest rate 4.5
    amortizeTerm: 60, // this amortizes for 5 years. This can stay as default 60 months
  });
  const monthlyPayment: number = Math.round(pathOr(0, ['paymentRound'], loan)); // if the payment in the returned json is missing its 0
  PMI = Number(calculatePMI(downPaymentPercent, homePrice, downPayment));
  gpay = Number(grossPay);
  return (
    ((Number(recurringPayments) +
      Number(monthlyPayment) +
      Number(liability) +
      Number(PMI)) /
      gpay) *
    100
  );
};

export const getPropertyTax = (details: any) => {
  return pathOr(
    0,
    ['listing', 'localized_fields', 'tax'],
    head(pathOr([{}], ['value'], details))
  );
};

export const getHomePrice = (details: any) => {
  return Math.round(
    pathOr(0, ['list_price'], head(pathOr([{}], ['value'], details)))
  );
};

export const getHomeInsurance = (details: any) => {
  return pathOr(0, ['monthly_premium_in_cents'], details);
};

export const getDefaultRate = (rates: any) => {
  return pathOr(0, ['interest_rate1'], head(rates));
};

export const getDefaultInstallment = (rates: any) => {
  const rates_for = pathOr('', ['rates_for'], head(rates));
  return rates_for ? parseInstallment(rates_for) : 0;
};

export const buildInterestRates = (details: any) => {
  const interest_rates = pathOr(
    [],
    ['rates'],
    head(pathOr([{}], ['items'], details))
  );
  return interest_rates.map((l: any) => mergeAll(l.rate_type));
};

export const parseInstallment = (pi: string) => {
  return replace(/[^0-9\.]/g, '', pi);
};

export const removePercent = (value: any) => {
  return replace(/%/g, '', value);
};

export const removePercentAndConvertToNumber = (value: any) => {
  return Number(replace(/%/g, '', value));
};

export const calculateDownPayment = (
  homePrice: number,
  downPaymentPercent: number
) => {
  return Number(homePrice) * (Number(downPaymentPercent) / 100);
};

export const calculateDownPaymentPercent = (
  downPayment: string,
  homePrice: string
) => {
  return Math.ceil((100 * Number(downPayment)) / Number(homePrice));
};

export const calculateBalancePayment = (homePrice: number, dpp: number) => {
  return Number(homePrice) - calculateDownPayment(homePrice, dpp);
};

export const calculateBalanceFromDownPayment = (
  homePrice: string,
  downPayment: string
) => {
  return Number(homePrice) - Number(downPayment);
};

export const nextInterestRate = (
  prevInterestRate: string,
  newStep: string,
  prevStep: string
) => {
  const interestRateNumber = Number(replace(/%/g, '', prevInterestRate));
  let step = Number(newStep);

  if (lt(Number(newStep), Number(prevStep))) {
    step = Number(prevStep) - Number(newStep);
    return (
      (
        interestRateNumber -
        step * DASHBOARD_USER_MOVEMENT_INTEREST_RATE_UP
      ).toString() + '%'
    );
  }
  step = Number(newStep) - Number(prevStep);
  return (
    (
      step * DASHBOARD_USER_MOVEMENT_INTEREST_RATE_UP +
      interestRateNumber
    ).toString() + '%'
  );
};
export const findClosest = (num: number, arr: number[]) => {
  let mid;
  let lo = 0;
  let hi = arr.length - 1;
  while (hi - lo > 1) {
    mid = Math.floor((lo + hi) / 2);
    if (arr[mid] < num) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  if (num - arr[lo] <= arr[hi] - num) {
    return arr[lo];
  }
  return arr[hi];
};

export const calculateDownPaymentGroup = (homePrice: number) => {
  const group = STANDARD_DOWNPAYMENT_PERCENTAGES.map((percent: number) =>
    calculateBalancePayment(homePrice, percent)
  );
  return group;
};

export const calculatePMI = (
  percent: number,
  homePrice: number,
  downPayment: number
) => {
  const loan_amount = homePrice - downPayment;
  if (percent >= 20) {
    return 0;
  } else {
    return Math.round((loan_amount * (0.7 / 100)) / 12).toFixed(2);
  }
};


export const getDecimalDigits = (val: string) => {
  const numberSegment = split(".", val)
  const isNumber = new RegExp(/^\d+/);
  const decimalDigits = isNumber.exec(numberSegment[1]) || [""]
  return decimalDigits[0]
}

// NOTE: While unformatting it rounds of 2,222 to just 2222
// therefore preventing the user from typing any float points,
// so we avoid formatting all together in those scenarios 
export const shouldSkipFormatting = (val: string) => {
  const decimalDigits = getDecimalDigits(val)
  const decimalDigitsSanitized = pipe(
    parseFloat,
    when(isNaN, () => -1) 
  )(decimalDigits);
  
  return {
    skip: equals(decimalDigitsSanitized,-1),
    val: decimalDigits
  }
}


export const hasCorrectFloatFormat = (value: string) => {
  const splitted = split('.', value);
  if (!includes('.', value)) {
    return false;
  }
  if (length(splitted) > 2) {
    return true;
  } else return length(splitted) === 1;
};

export const hasCorrectIntFormat = (value: string) => {
  const re = /^[0-9\b]+$/;
  return isEmpty(value) || test(re, value);
};

export const findCreditScores = (data: any) => {
  const scores = pathOr('', ['average_score'], data);
  if (isEmpty(scores) || equals(scores, '0')) {
    return 0;
  } else {
    return scores;
  }
};

export const findInterestRate = (products: any, pathComponents: any) => {
  const year = pathComponents[0].split('-')[0];
  const f = (e: any) => equals(Number(year), Number(e.years));
  const product = filter(f, products);
  if (isEmpty(product)) {
    return pathComponents[1];
  } else {
    return products[0].interest_rate1;
  }
};

export const satisfiedProductsforThe = (data: any) => {
  const filtered: any = [];
  data.map((e: any) => {
    if (e.rates_for.includes('-Year Fixed')) {
      filtered.push(e);
    }
  });
  return filtered;
};

export const buildLoanType = (year: any, percent: any) => {
  return year + '-Year Fixed_' + percent;
};

export const joinWithConnector = (
  stringSet: string[],
  connector: string = ', '
) => {
  return pipe(
    map(trim),
    filter((e) => not(isEmpty(e))),
    join(connector),
    trim
  )(stringSet);
};

export const getDefaultHomeInsurance = (homePrice: any) => {
  return Math.round(homePrice * (0.035 / 100));
};

export const getDefaultPropertyTax = (homePrice: any) => {
  return Math.round(homePrice * (0.155 / 100) * 12);
};
